import {
  Component,
  ChangeDetectionStrategy,
  inject,
  computed,
  viewChild,
  viewChildren,
  ElementRef,
  signal,
  SecurityContext,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { toIsoCurrency } from '../../../../../../shared/utils/xtm-currency.utils';
import { FormatCurrencyPipe } from '../../../../../../shared/pipes/format-currency.pipe';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
  MatDialogContent,
} from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import {
  Estimate,
  FuzzyBreakdown,
  FuzzyBreakdownEstimate,
  VendorDetails,
  VendorOrder,
  Cost,
} from '../../../../../../shared/models/vendor-order.model';
import { CanvasService } from '../../../../../../shared/services/canvas.service';
import { SpinnerComponent } from '../../../../../../shared/components/az-spinner/spinner.component';
import { DownloadSuccessDialogComponent } from '../../../../../../shared/components/download-success-dialog/download-success-dialog.component';
import { Locale, LOCALES } from '../../../../../../shared/config/locales';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export interface SowModalData {
  order: VendorOrder;
  estimate: Estimate;
  breakdown?: FuzzyBreakdown;
  vendor?: VendorDetails;
}

@Component({
  selector: 'app-quote-sow-modal',
  standalone: true,
  imports: [FormatCurrencyPipe, MatDialogContent, SpinnerComponent],
  templateUrl: './quote-sow-modal.component.html',
  styleUrl: './quote-sow-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuoteSowModalComponent {
  private readonly dialogRef = inject(MatDialogRef<QuoteSowModalComponent>);
  private readonly dialog = inject(MatDialog);
  private readonly canvasService = inject(CanvasService);
  private readonly translateService = inject(TranslateService);
  private readonly sanitizer = inject(DomSanitizer);
  readonly data: SowModalData = inject(MAT_DIALOG_DATA);

  readonly pdfContent = viewChild<ElementRef<HTMLElement>>('pdfContent');
  readonly breakdownTables =
    viewChildren<ElementRef<HTMLElement>>('breakdownTableEl');
  readonly disclaimerElement =
    viewChild<ElementRef<HTMLElement>>('disclaimerEl');

  readonly loading = signal(false);

  readonly order = computed(() => this.data.order);
  readonly estimate = computed(() => this.data.estimate);
  readonly vendor = computed(() => this.data.vendor);
  readonly breakdown = computed(() => this.data.breakdown);

  readonly vendorName = computed(
    () => this.estimate().name ?? 'Unknown Vendor',
  );

  readonly sowCreationDate = computed(() => {
    const now = new Date();
    const day = now.getDate().toString().padStart(2, '0');
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const year = now.getFullYear();
    return `${day}/${month}/${year}`;
  });

  readonly currency = computed(() => {
    const est = this.estimate();
    return toIsoCurrency(est.currency ?? est.quote?.normal?.currency);
  });

  readonly jobName = computed(() => this.order().customJobName ?? '--');
  readonly jobNumber = computed(() => {
    const costId = this.estimate().costId;
    return costId ? `AZT-${costId}` : (this.order().jobNumber ?? '--');
  });

  readonly preferredDeliveryDate = computed(() => {
    const dl = this.order().deadline ?? this.order().fixedDeadline;
    if (!dl) return '--';
    const d = new Date(dl);
    if (Number.isNaN(d.getTime())) return '--';
    return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
  });

  readonly supplierName = computed(() => this.estimate().name ?? '--');
  readonly contactEmail = computed(() => 'AZTranslate@astrazeneca.com');

  readonly vendorLegalName = computed(
    () => this.vendor()?.legalEntityName ?? this.vendorName(),
  );
  readonly vendorAddress = computed(() => this.vendor()?.address);

  readonly sourceLocale = computed(() => this.order().sourceLocaleId ?? '');
  readonly targetLocales = computed(() => {
    const targets = this.order().targetLocaleId;
    if (!targets) return '';
    return Array.isArray(targets) ? targets.join(', ') : targets;
  });

  readonly languageDirection = computed(() => {
    const source = this.sourceLocale();
    const target = this.targetLocales();
    if (!source && !target) return '';
    const sourceName = this.getLanguageName(source);
    const targets = this.order().targetLocaleId;
    const targetNames = Array.isArray(targets)
      ? targets
          .map((t) => this.getLanguageName(t))
          .sort()
          .join(', ')
      : this.getLanguageName(target);
    return `${sourceName} → ${targetNames}`;
  });

  readonly costs = computed<Cost[]>(() => {
    const est = this.estimate();
    const quote = est.quote?.normal ?? est.quote?.rushed;
    if (quote?.costs?.length) return quote.costs;
    const totalCost = quote?.totalCost ?? est.priceMax ?? est.priceMin;
    if (totalCost != null) {
      return [{ concept: 'Estimated linguistic review', cost: totalCost }];
    }
    return [];
  });

  readonly totalCost = computed(() => {
    const est = this.estimate();
    const quote = est.quote?.normal ?? est.quote?.rushed;
    return quote?.totalCost ?? est.priceMax ?? est.priceMin ?? 0;
  });

  readonly deliveryDays = computed(() => this.order().deliveryDays ?? 1);
  pdfHeightInPX = 1585;
  pageTopOffset = 20;
  pageMarginBottom = 75;
  pageMarginTop = 75;
  maxHeightPerPage =
    this.pdfHeightInPX - this.pageTopOffset - this.pageMarginBottom;
  pdfFile!: jsPDF;
  // Breakdown table configuration
  readonly breakdownCols = [
    'Source Language',
    'Target Language',
    'Step Name',
    'Currency Cost',
    'Word Count',
    'Fuzzy Range Code',
    'Estimated Cost Min',
    'Estimated Cost Max',
  ];
  readonly numericCols = [
    'Word Count',
    'Weighted Word Count',
    'Character Count',
    'Estimated Cost Min',
    'Estimated Cost Max',
  ];
  readonly subtotalRow = 'Subtotal';
  readonly hiddenColsForSubtotalRow = ['Source Language', 'Target Language'];

  readonly projectName = computed(
    () =>
      this.breakdown()?.['Project Name'] ?? this.order().xtmProjectName ?? '',
  );

  readonly smartlingJobName = computed(
    () => this.order().smartlingJobName ?? '',
  );

  readonly disclaimer = computed<string | null>(() => {
    const v = this.vendor();
    if (!v?.disclaimer) return '';
    return this.sanitizer.sanitize(
      SecurityContext.HTML,
      v.disclaimer.replace(/\n/g, '<br />'),
    );
  });

  readonly sortedEstimateEntries = computed(() => {
    const estimates = this.breakdown()?.estimates;
    if (!estimates) return [];
    return Object.entries(estimates).sort(([keyA], [keyB]) => {
      const localeA = LOCALES.find(
        (l: Locale) => l.code === keyA || l.xtmCode === keyA,
      );
      const localeB = LOCALES.find(
        (l: Locale) => l.code === keyB || l.xtmCode === keyB,
      );
      return (localeA?.name ?? keyA).localeCompare(localeB?.name ?? keyB);
    });
  });

  getLanguageName(code: string): string {
    const locale = LOCALES.find(
      (l: Locale) => l.code === code || l.xtmCode === code,
    );
    if (locale) return locale.name;
    const resolved = this.resolveLocaleCode(code);
    const key = `languages.${resolved}`;
    const result = this.translateService.instant(key);
    return result !== key ? result : code;
  }

  isNumericCol(col: string): boolean {
    return this.numericCols.includes(col);
  }

  isSubtotalRow(estimate: FuzzyBreakdownEstimate, isLast: boolean): boolean {
    return isLast && estimate['Fuzzy Range Code'] === this.subtotalRow;
  }

  isHiddenForSubtotal(col: string): boolean {
    return this.hiddenColsForSubtotalRow.includes(col);
  }

  getCellValue(
    estimate: FuzzyBreakdownEstimate,
    col: string,
    isLast: boolean,
  ): string {
    if (this.isSubtotalRow(estimate, isLast) && this.isHiddenForSubtotal(col))
      return '';
    const value = (estimate as unknown as Record<string, string>)[col] ?? '';
    if (this.numericCols.includes(col) && value) {
      const num = parseFloat(value);
      return isNaN(num) ? value : num.toLocaleString();
    }
    return value;
  }

  private resolveLocaleCode(key: string): string {
    const locale = LOCALES.find(
      (l: Locale) => l.code === key || l.xtmCode === key,
    );
    return locale?.code ?? key;
  }

  async generateQuotePDF(): Promise<void> {
    const pdfContentEl = this.pdfContent();
    if (!pdfContentEl) return;
    this.loading.set(true);
    this.pdfFile = new jsPDF('p', 'mm', 'a4');

    const pdfElement = pdfContentEl.nativeElement;
    const canvas = await html2canvas(pdfElement, {
      scale: 3,
      windowWidth: 1600,
      onclone: (documentClone: Document, clonedElement: HTMLElement) => {
        clonedElement.style.width = 'max-content';
        clonedElement.style.minWidth = 'max-content';
        clonedElement.style.overflow = 'visible';
        let parent = clonedElement.parentElement;
        while (parent) {
          parent.style.overflow = 'visible';
          parent.style.maxWidth = 'none';
          parent.style.width = 'auto';
          parent = parent.parentElement;
        }
        this.prepareHTML(documentClone);
      },
    });

    const imageGeneratedFromTemplate = canvas.toDataURL('image/jpeg', 1.0);
    const margin = 15;
    const fileWidth = 210 - 2 * margin;
    const generatedImageHeight = (canvas.height * fileWidth) / canvas.width;

    await this.downloadQuotePDF(
      imageGeneratedFromTemplate,
      fileWidth,
      generatedImageHeight,
      margin,
    );
    this.showDownloadSuccess();
    this.loading.set(false);
  }

  prepareHTML(documentClone: Document) {
    const quoteDetails =
      documentClone.getElementById('quoteDetails') ||
      document.createElement('div');
    const pdfContent =
      documentClone.getElementById('pdfContent') ||
      document.createElement('div');
    const costs =
      documentClone.getElementById('costs') || document.createElement('div');
    const header =
      documentClone.getElementById('header') || document.createElement('div');
    const orderDetails =
      documentClone.getElementById('orderDetails') ||
      document.createElement('div');
    const quoteTotal =
      documentClone.getElementById('quoteTotal') ||
      document.createElement('div');

    let contentHeight = pdfContent.offsetHeight;

    if (contentHeight > this.maxHeightPerPage) {
      let heightLeft =
        this.maxHeightPerPage - header.offsetHeight - orderDetails.offsetHeight;
      if (heightLeft >= quoteDetails.offsetHeight) {
        heightLeft -= quoteDetails.offsetHeight;

        if (heightLeft >= quoteTotal.offsetHeight) {
          heightLeft -= quoteTotal.offsetHeight;
          const extraMargin =
            this.pageMarginBottom + this.pageMarginTop + this.pageTopOffset;
          heightLeft += extraMargin;
          contentHeight += heightLeft;
          quoteTotal.style.marginBottom = `${heightLeft}px`;
        } else {
          const extraMargin =
            heightLeft +
            this.pageMarginBottom +
            this.pageMarginTop +
            this.pageTopOffset;
          contentHeight += extraMargin;
          quoteTotal.style.marginTop = `${extraMargin}px`;
        }
      } else {
        const costRowHeight = 47;
        const rows = Math.floor(heightLeft / costRowHeight);
        const children = costs.children;
        const index = rows - 1;
        const child = children[index] as HTMLElement;
        const overloadedRows = children.length - rows;
        const overloadedContentHeight = overloadedRows * costRowHeight;

        const remainingSpace =
          quoteDetails.offsetHeight - overloadedContentHeight - heightLeft;
        const separatorHeight =
          remainingSpace + this.pageMarginBottom + this.pageMarginTop;

        const separator = document.createElement('div');
        separator.style.height = `${separatorHeight}px`;

        const difference = quoteDetails.offsetHeight - heightLeft;

        if (difference >= costRowHeight) {
          separator.style.borderBottom = '1px solid #CBCBCB';
        }

        child?.parentNode?.insertBefore(separator, child.nextSibling);

        const extraMargin = 20;
        const quoteDetailsMarginBottom = separatorHeight + extraMargin;
        quoteDetails.style.marginBottom = `${quoteDetailsMarginBottom}px`;

        contentHeight += separatorHeight;
      }

      pdfContent.style.height = `${contentHeight}px`;
    }
  }

  async downloadQuotePDF(
    imageData: string,
    width: number,
    height: number,
    margin: number,
  ) {
    const fileName = this.data.order.customJobName + '.pdf';
    const pageHeight = 295;
    const offset = 5;
    let position = offset;
    let heightLeft = height;
    heightLeft -= pageHeight;

    this.pdfFile.addImage(imageData, 'PNG', margin, position, width, height);

    while (heightLeft >= 0) {
      position = heightLeft - height;
      this.pdfFile.addPage();
      this.pdfFile.addImage(imageData, 'PNG', margin, position, width, height);
      heightLeft -= pageHeight;
    }

    // Add each language pair in new page
    for (let estimate of this.breakdownTables()) {
      const breakdownCanvas = await this.canvasService.createCanvasFromHTML(
        estimate.nativeElement,
      );
      if (breakdownCanvas) {
        const breakdownImg = breakdownCanvas.toDataURL('image/png', 1.0);
        const breakdownImgHeight =
          (breakdownCanvas.height * width) / breakdownCanvas.width;

        this.pdfFile.addPage();
        this.pdfFile.addImage(
          breakdownImg,
          'PNG',
          margin,
          offset,
          width,
          breakdownImgHeight,
        );
      }
    }

    // Add disclaimer at the end in new page
    const disclaimerEl = this.disclaimerElement();
    if (disclaimerEl) {
      const disclaimerCanvas = await this.canvasService.createCanvasFromHTML(
        disclaimerEl.nativeElement,
      );
      if (disclaimerCanvas) {
        const disclaimerImg = disclaimerCanvas.toDataURL('image/jpeg', 1.0);
        const disclaimerImgHeight =
          (disclaimerCanvas.height * width) / disclaimerCanvas.width;

        this.pdfFile.addPage();
        this.pdfFile.addImage(
          disclaimerImg,
          'JPEG',
          margin,
          offset,
          width,
          disclaimerImgHeight,
        );
      }
    }

    this.pdfFile.save(fileName);
  }

  close(): void {
    this.dialogRef.close();
  }

  async download(): Promise<void> {
    await this.generateQuotePDF();
  }

  private showDownloadSuccess(): void {
    this.dialog.open(DownloadSuccessDialogComponent, {
      width: '560px',
      maxWidth: '95vw',
      panelClass: 'no-padding-dialog',
      disableClose: false,
    });
  }
}
